export const COGTOSHI_PER_COG = 100_000_000n;
export const SATS_PER_BTC = 100_000_000n;

const DECIMALS = 8;

export function parseIntegerString(s: string): bigint {
  if (!/^-?(0|[1-9][0-9]*)$/.test(s)) {
    throw new Error(`invalid integer string: ${JSON.stringify(s)}`);
  }
  return BigInt(s);
}

// explorer-api drops null fields on serialization, so optional bigint-bearing
// strings arrive as `undefined`. Hydrators map them to `null`.
export function optionalIntegerString(s: string | undefined): bigint | null {
  return s === undefined ? null : parseIntegerString(s);
}

export interface FormatOptions {
  trimTrailingZeros?: boolean;
  minDecimals?: number;
  groupIntegerDigits?: boolean;
}

function formatFixedDecimal(value: bigint, options: FormatOptions): string {
  const trim = options.trimTrailingZeros ?? true;
  const minDecimals = Math.max(0, Math.min(DECIMALS, options.minDecimals ?? 0));
  const group = options.groupIntegerDigits ?? false;

  const negative = value < 0n;
  const abs = negative ? -value : value;
  const divisor = COGTOSHI_PER_COG;
  const integerPart = abs / divisor;
  const fractionalPart = abs % divisor;

  let fractional = fractionalPart.toString().padStart(DECIMALS, "0");
  if (trim) {
    fractional = fractional.replace(/0+$/, "");
    if (fractional.length < minDecimals) {
      fractional = fractional.padEnd(minDecimals, "0");
    }
  }

  const integerStr = group ? groupDigits(integerPart) : integerPart.toString();
  const sign = negative ? "-" : "";
  return fractional.length > 0 ? `${sign}${integerStr}.${fractional}` : `${sign}${integerStr}`;
}

function groupDigits(n: bigint): string {
  const s = n.toString();
  return s.replace(/\B(?=(\d{3})+(?!\d))/g, ",");
}

export function formatCog(cogtoshi: bigint, options: FormatOptions = {}): string {
  return formatFixedDecimal(cogtoshi, options);
}

export function formatBtc(sats: bigint, options: FormatOptions = {}): string {
  return formatFixedDecimal(sats, options);
}

const RATIO_SCALE = 10_000n;
const RATIO_DECIMALS = 4;

// Mining efficiency: cogtoshi won per sat spent. Both legs are bigint;
// division happens after scaling so the result has 4 decimal places of
// precision without crossing through Number.
export function formatCogtoshiPerSat(cogtoshi: bigint, sats: bigint): string {
  if (sats === 0n) return cogtoshi === 0n ? "0" : "∞";
  const scaled = (cogtoshi * RATIO_SCALE) / sats;
  const negative = scaled < 0n;
  const abs = negative ? -scaled : scaled;
  const integerPart = abs / RATIO_SCALE;
  const fractionalPart = abs % RATIO_SCALE;
  const fractional = fractionalPart
    .toString()
    .padStart(RATIO_DECIMALS, "0")
    .replace(/0+$/, "");
  const sign = negative ? "-" : "";
  return fractional.length > 0
    ? `${sign}${integerPart.toString()}.${fractional}`
    : `${sign}${integerPart.toString()}`;
}
